import { useState, useEffect, useRef } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import LogModal from '../../components/Logs/LogModal';

import { getSettings, updateSettings } from '../../services/SettingsService';
import { getSymbols } from '../../services/SymbolsService';
import SwitchInput from '../../components/SwitchInput';

function parseBlockedCoins(raw) {
    if (Array.isArray(raw))
        return uniqueCoins(raw);
    const text = String(raw || '').trim();
    if (!text) return [];
    return uniqueCoins(text.split(/[,;\s]+/));
}

const BLOCK_ALIASES = {
    SHIBA: 'SHIB',
    SHIBAINU: 'SHIB',
    DOGECOIN: 'DOGE',
    BITCOIN: 'BTC',
    ETHEREUM: 'ETH'
};

function uniqueCoins(list) {
    const seen = new Set();
    const coins = [];
    for (const item of list || []) {
        let token = String(item || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (!token) continue;
        if (token.endsWith('USDC'))
            token = token.slice(0, -4);
        else if (token.endsWith('USDT'))
            token = token.slice(0, -4);
        token = BLOCK_ALIASES[token] || token;
        if (token.length < 2 || seen.has(token)) continue;
        seen.add(token);
        coins.push(token);
    }
    return coins;
}

function Settings() {

    const confirmPassword = useRef('');
    const [settings, setSettings] = useState({});
    const [notification, setNotification] = useState({});
    const [blockedCoins, setBlockedCoins] = useState([]);
    const [blockedDraft, setBlockedDraft] = useState('');
    const [usdcBases, setUsdcBases] = useState([]);

    useEffect(() => {

        getSymbols()
            .then(result => {
                const rows = result?.rows || result || [];
                const bases = uniqueCoins(rows
                    .map(item => item.symbol || item)
                    .filter(symbol => String(symbol || '').toUpperCase().endsWith('USDC')));
                setUsdcBases(bases);
            })
            .catch(() => setUsdcBases([]));

        getSettings()
            .then(result => {
                setSettings(result);
                setBlockedCoins(parseBlockedCoins(result.predadorBlockedCoins));
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
    }, []);

    function onInputChange(event) {
        if (event.target.type === 'checkbox' || event.target.id === 'useRedis') {
            setSettings(prevState => ({ ...prevState, [event.target.id]: event.target.checked || event.target.value }));
        } else {
            setSettings(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
        }
    }

    function addBlockedCoin() {
        const next = uniqueCoins([...blockedCoins, ...String(blockedDraft || '').split(/[,;\s]+/)]);
        setBlockedCoins(next);
        setBlockedDraft('');
    }

    function removeBlockedCoin(coin) {
        setBlockedCoins(prev => prev.filter(item => item !== coin));
    }

    function onBlockedKeyDown(event) {
        if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
            event.preventDefault();
            addBlockedCoin();
        }
    }

    function onFormSubmit(event) {
        if ((settings.password || confirmPassword.current.value)
            && settings.password !== confirmPassword.current.value)
            return setNotification({ type: 'error', text: `Os campos Nova Senha e Confirmar Senha devem ser iguais.` });

        // verifica se está mudando useRedis
        const isChangingRedis = settings.useRedis !== undefined;

        updateSettings({
            ...settings,
            predadorBlockedCoins: blockedCoins.join(',')
        })
            .then(result => {
                if (result) {
                    if (isChangingRedis) {
                        setNotification({ 
                            type: 'warning', 
                            text: `Settings saved! IMPORTANT: Please restart the server for Redis configuration change to take effect.` 
                        });
                    } else {
                        setNotification({ type: 'success', text: `Settings saved successfully!` });
                    }
                }
                else
                    setNotification({ type: 'error', text: result });
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                const errorMsg = err.response ? err.response.data : err.message;
                if (isChangingRedis) {
                    setNotification({ 
                        type: 'warning', 
                        text: `Settings saved in database. Error updating cache: ${errorMsg}. Please restart the server.` 
                    });
                } else {
                    setNotification({ type: 'error', text: errorMsg });
                }
            })
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">Configurações</h1>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <div className="d-inline-flex align-items-center">
                            <button id="btnLogs" className="btn btn-primary animate-up-2 me-2" data-bs-toggle="modal" data-bs-target="#modalLogs">
                                <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2h-1.528A6 6 0 004 9.528V4z" /><path fillRule="evenodd" d="M8 10a4 4 0 00-3.446 6.032l-1.261 1.26a1 1 0 101.414 1.415l1.261-1.261A4 4 0 108 10zm-2 4a2 2 0 114 0 2 2 0 01-4 0z" clipRule="evenodd" /></svg>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 mb-4">Configurações Pessoais</h2>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="email">Email</label>
                                        <input className="form-control" id="email" type="email" placeholder="nome@empresa.com" defaultValue={settings.email} onChange={onInputChange} />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="phone">Celular</label>
                                        <input className="form-control" id="phone" type="text" placeholder="+55 51 999999999" defaultValue={settings.phone} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div>
                                        <label htmlFor="password">Nova senha</label>
                                        <input className="form-control" id="password" type="password" placeholder="Digite a nova senha" onChange={onInputChange} />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div>
                                        <label htmlFor="confirmPassword">Confirmar senha</label>
                                        <input ref={confirmPassword} className="form-control" id="confirmPassword" type="password" placeholder="Repita a nova senha" />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap">
                                    <div className="col-sm-3">
                                        <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>Salvar</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 my-4">Configurações do Sistema</h2>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <SwitchInput 
                                            id="useRedis" 
                                            text="Usar Redis para memória do robô" 
                                            onChange={onInputChange} 
                                            isChecked={settings.useRedis !== undefined ? settings.useRedis : true} 
                                        />
                                        <small className="text-muted d-block mt-2">
                                            Se desabilitado, o sistema usará memória local (sem Redis). 
                                            <strong>Recomendado reiniciar o servidor após alterar esta configuração.</strong>
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="walletBalanceRefreshHours">Atualização do saldo na tela do cliente (horas)</label>
                                        <input
                                            className="form-control"
                                            id="walletBalanceRefreshHours"
                                            type="number"
                                            min="1"
                                            max="168"
                                            step="1"
                                            value={settings.walletBalanceRefreshHours ?? 1}
                                            onChange={onInputChange}
                                        />
                                        <small className="text-muted d-block mt-2">
                                            1 = a cada 1 hora. O saldo que o cliente vê na carteira só muda nesse intervalo.
                                            No painel admin o saldo continua ao vivo, sem atraso.
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap">
                                    <div className="col-sm-3">
                                        <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>Salvar</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 my-4">Predador · moedas bloqueadas</h2>
                            <p className="text-muted small">
                                Essas moedas não entram na caça automática de nenhum usuário.
                                Posições já abertas continuam até vender. Use o ticker da Binance, por exemplo SHIB (não “Shiba”), PEPE, TRUMP.
                            </p>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {blockedCoins.length
                                    ? blockedCoins.map(coin => (
                                        <span key={coin} className="badge bg-danger d-inline-flex align-items-center" style={{ fontSize: '0.85rem' }}>
                                            {coin}
                                            <button
                                                type="button"
                                                className="btn btn-link text-white p-0 ms-2"
                                                style={{ lineHeight: 1, textDecoration: 'none' }}
                                                onClick={() => removeBlockedCoin(coin)}
                                                aria-label={`Remover ${coin}`}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))
                                    : <span className="text-muted small">Nenhuma moeda bloqueada.</span>}
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="input-group">
                                        <input
                                            className="form-control"
                                            type="text"
                                            list="predador-blocked-coins"
                                            placeholder="SHIB"
                                            value={blockedDraft}
                                            onChange={event => setBlockedDraft(event.target.value)}
                                            onKeyDown={onBlockedKeyDown}
                                        />
                                        <datalist id="predador-blocked-coins">
                                            {usdcBases.map(coin => (
                                                <option key={coin} value={coin} />
                                            ))}
                                        </datalist>
                                        <button type="button" className="btn btn-outline-gray-600" onClick={addBlockedCoin}>
                                            Bloquear
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-3">
                                    <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>Salvar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 my-4">Taxa da plataforma (coleta de fees)</h2>
                            <p className="text-muted small">
                                Quando o utilizador coleta taxas da posição, o app calcula 1% (por defeito) sobre as taxas
                                pendentes, pede assinatura de transferência SPL para esta carteira e só depois continua
                                com o saque ou encerramento.
                            </p>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="orcaCreditsPaymentWallet">Carteira de recebimento da taxa</label>
                                        <input
                                            className="form-control"
                                            id="orcaCreditsPaymentWallet"
                                            type="text"
                                            placeholder="Endereço Solana"
                                            defaultValue={settings.orcaCreditsPaymentWallet || ''}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-3">
                                    <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>Salvar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 my-4">Configurações de Alertas</h2>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="mailerSendApiKey">MailerSend API Key</label>
                                        <input className="form-control" id="mailerSendApiKey" type="password" placeholder="Informe a MailerSend API Key (obtenha em app.mailersend.com)" defaultValue={settings.mailerSendApiKey} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="mailerSendFromEmail">MailerSend From Email</label>
                                        <input className="form-control" id="mailerSendFromEmail" type="email" placeholder="ex: no-reply@seudominio.com (domínio verificado no MailerSend)" defaultValue={settings.mailerSendFromEmail} onChange={onInputChange} />
                                        <small className="text-muted d-block mt-2">
                                            Este campo define o remetente dos emails. Se vazio, o sistema usa o email principal das configurações.
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="twilioPhone">Telefone Twilio</label>
                                        <input className="form-control" id="twilioPhone" type="text" placeholder="Informe o número de telefone Twilio" defaultValue={settings.twilioPhone} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="twilioSid">Twilio SID</label>
                                        <input className="form-control" id="twilioSid" type="text" placeholder="Informe o Twilio SID" defaultValue={settings.twilioSid} onChange={onInputChange} />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="twilioToken">Twilio Token</label>
                                        <input className="form-control" id="twilioToken" type="password" placeholder="Informe o Twilio Token" defaultValue={settings.twilioToken} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="telegramBot">Nome do Bot do Telegram</label>
                                        <input className="form-control" id="telegramBot" type="text" placeholder="Informe o nome do Bot do Telegram" defaultValue={settings.telegramBot} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="telegramToken">Token do Bot do Telegram</label>
                                        <input className="form-control" id="telegramToken" type="password" placeholder="Informe o Token do Bot do Telegram" defaultValue={settings.telegramToken} onChange={onInputChange} />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="telegramChat">ID do chat do Telegram</label>
                                        <input className="form-control" id="telegramChat" type="text" placeholder="Informe o ID do chat do Telegram" defaultValue={settings.telegramChat} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="openaiKey">OpenAI API Key</label>
                                        <input className="form-control" id="openaiKey" type="password" placeholder="Informe a chave da API OpenAI (sk-...)" defaultValue={settings.openaiKey} onChange={onInputChange} />
                                        <small className="text-muted d-block mt-2">
                                            Chave da API OpenAI para o Agente IA. Obtenha em <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com/api-keys</a>
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap">
                                    <div className="col-sm-3">
                                        <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>Salvar</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
            <Toast text={notification.text} type={notification.type} />
            <LogModal file="system" />
        </ >
    );
}

export default Settings;
